/**
 * @file OutsideHaulierPohDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-26
 * @modified 2026-10-26
 * @description "<Haulier> is an outside haulier and SO-D… has no PO-H. Raise
 *              one to <supplier> and send it?" — asked on a planning board
 *              straight after an outside haulier is set on an order's row
 *              (Cam, 26 Oct 2026). Raise PO-H changes the order to
 *              third-party haulage through the order save itself, raises the
 *              PO-H and opens the Send to Supplier window for it there and
 *              then (SendPOEmailModal, as the PO window); Not now leaves the
 *              order as it is.
 *
 *              Everything the window says — what changes, where the order is
 *              left on the board, why Raise is greyed — is the server's
 *              (GET /api/transport/planning/outside-haulier-poh, rule
 *              utils/outsideHaulierPoh.js), worked out again whenever the
 *              supplier, the link tick or a pallets figure changes.
 *
 *              Modes: 'raise' (Raise PO-H, with the supplier picker and
 *              "Link … for next time" when the haulier has no supplier, and
 *              the pallets of packed lines); 'send' (the order already has an
 *              unsent PO-H to this haulier — Send PO-H…); 'blocked' (why
 *              nothing can be raised here — Close).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import Modal from './Modal';
import SearchableDropdown from './SearchableDropdown';
import TickCheckbox from './TickCheckbox';
import LoadingSpinner from './LoadingSpinner';
import SendPOEmailModal from './SendPOEmailModal';
import { useSlideOutButton } from './SlideOutButton';
import { formatCount } from '../utils/numberFormatters';
import './OutsideHaulierPohDialog.css';

const qs = (o) => Object.entries(o)
  .filter(([, v]) => v !== null && v !== undefined && v !== '')
  .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');

/** The body — its own component so the footer's slide-outs measure when it mounts. */
function DialogBody({ plan, suppliers, supplierId, setSupplierId, link, setLink, pallets, setPallets, error, reloading }) {
  useSlideOutButton([plan?.mode, plan?.raiseBlock, reloading]);
  if (!plan) return <LoadingSpinner />;
  const hName = plan.haulier?.name || 'The haulier';
  const supplierOptions = (suppliers || []).map(s => ({
    value: String(s.id), label: s.company_name, badge: s.account_no || undefined,
    searchText: `${s.company_name} ${s.account_no || ''} ${s.email || ''}`
  }));
  const chosen = supplierOptions.find(o => o.value === String(supplierId || ''));
  return (
    <div className="ohp-body">
      <p className="ohp-question">{plan.mode === 'blocked' ? plan.title : plan.question}</p>

      {plan.mode === 'blocked' && (
        <p className="ohp-block">
          <span className="material-symbols-rounded">info</span>
          <span>{plan.block}</span>
        </p>
      )}

      {plan.mode === 'send' && (
        <p className="ohp-note">
          Send it now from the Send to Supplier window — the same one as in the PO window. Not now leaves it unsent.
        </p>
      )}

      {plan.mode === 'raise' && plan.needsSupplier && (
        <div className="ohp-field">
          <label htmlFor="ohp-supplier">Supplier {hName} bills as <span className="required-field">*</span></label>
          <SearchableDropdown
            id="ohp-supplier"
            escapeOverflow
            value={supplierId ? String(supplierId) : ''}
            onChange={(v) => setSupplierId(v || '')}
            options={supplierOptions}
            placeholder={suppliers ? 'Choose the supplier the PO-H goes to…' : 'Loading suppliers…'}
            searchPlaceholder="Type a supplier name..."
            disabled={!suppliers}
          />
          <span className="ohp-hint">{hName} has no supplier linked in Transport Settings → Hauliers. The PO-H goes to the supplier chosen here.</span>
          {plan.offerLink !== false && (
            <TickCheckbox
              id="ohp-link"
              checked={!!link}
              onChange={(e) => setLink(e.target.checked)}
              disabled={!chosen}
              label={chosen ? `Link ${chosen.label} to ${hName} for next time` : `Link the supplier to ${hName} for next time`}
            />
          )}
        </div>
      )}

      {plan.mode === 'raise' && (plan.palletLines || []).length > 0 && (
        <div className="ohp-field">
          <label>Pallets — the haulier is booked by the pallet <span className="required-field">*</span></label>
          <table className="ohp-pallets">
            <thead>
              <tr><th>Line</th><th className="num">Quantity</th><th className="num">Pallets</th></tr>
            </thead>
            <tbody>
              {plan.palletLines.map(l => (
                <tr key={l.id}>
                  <td>{l.code ? <span className="ohp-code">{l.code}</span> : null}{l.name}</td>
                  <td className="num">{l.quantity == null ? '—' : `${formatCount(l.quantity)}${l.unit ? ` × ${l.unit}` : ''}`}</td>
                  <td className="num">
                    <input
                      type="text"
                      inputMode="decimal"
                      className="styled-input ohp-pallets-input"
                      aria-label={`Pallets for ${l.code || l.name || 'this line'}`}
                      value={pallets[l.id] ?? ''}
                      onChange={(e) => setPallets(prev => ({ ...prev, [l.id]: e.target.value }))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {plan.mode === 'raise' && (plan.changes || []).length > 0 && (
        <div className="ohp-field">
          <label>What will change</label>
          <ul className="ohp-changes">
            {plan.changes.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </div>
      )}

      {plan.mode === 'raise' && plan.board && (
        <p className={`ohp-board ${plan.board.effect === 'stays' ? 'is-stays' : 'is-leaves'}`}>
          <span className="material-symbols-rounded">{plan.board.effect === 'stays' ? 'local_shipping' : 'move_up'}</span>
          <span>{plan.board.text}</span>
        </p>
      )}

      {plan.mode === 'raise' && plan.raiseBlock && <p className="ohp-error">{plan.raiseBlock}</p>}
      {error && <p className="ohp-error">{error}</p>}
    </div>
  );
}

/**
 * @param {{ request: { date, orderId, orderNumber, haulier }, user, onClose: (changed: boolean, notes?: string[]) => void }} props
 */
function OutsideHaulierPohDialog({ request, user, onClose }) {
  const [plan, setPlan] = useState(null);
  const [suppliers, setSuppliers] = useState(null);
  const [supplierId, setSupplierId] = useState('');
  const [link, setLink] = useState(true);
  const [pallets, setPallets] = useState({});
  const [reloading, setReloading] = useState(false);
  const [raising, setRaising] = useState(false);
  const [error, setError] = useState(null);
  const [sendPo, setSendPo] = useState(null);
  const [changed, setChanged] = useState(false);
  const notesRef = useRef([]);
  const firstLoad = useRef(true);
  // The board's callback, read when needed (a new one each render must not reload the plan).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const load = useCallback(async () => {
    setReloading(true);
    try {
      const res = await fetch(`${API_URL}/api/transport/planning/outside-haulier-poh?${qs({
        date: request.date, orderId: request.orderId, haulier: request.haulier,
        supplierAccountId: supplierId || null, link: link ? 'true' : 'false',
        pallets: Object.keys(pallets).length ? JSON.stringify(pallets) : null
      })}`, { credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) throw new Error(body.error || `Could not check the PO-H (${res.status})`);
      const p = body.plan;
      // Nothing to ask (a sent PO-H, a past day…): never a window at all.
      if (!p || !p.prompt) { if (firstLoad.current) onCloseRef.current(false); return; }
      firstLoad.current = false;
      setPlan(p);
      setError(null);
    } catch (err) {
      if (firstLoad.current) { onCloseRef.current(false); return; }
      setError(err.message);
    } finally {
      setReloading(false);
    }
  }, [request.date, request.orderId, request.haulier, supplierId, link, pallets]);

  // Worked out again as the choices change (pallets typed: after a pause).
  useEffect(() => {
    const t = setTimeout(load, firstLoad.current ? 0 : 300);
    return () => clearTimeout(t);
  }, [load]);

  const needsSuppliers = !!plan?.needsSupplier;
  useEffect(() => {
    if (!needsSuppliers || suppliers) return undefined;
    let cancelled = false;
    fetch(`${API_URL}/api/contacts/search?contactStatus=ACTIVE&accountType=SUPPLIER&limit=10000`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!cancelled) setSuppliers(d.contacts || []); })
      .catch(() => { if (!cancelled) setSuppliers([]); });
    return () => { cancelled = true; };
  }, [needsSuppliers, suppliers]);

  const raise = async () => {
    if (!plan || plan.mode !== 'raise' || plan.raiseBlock || raising) return;
    setRaising(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/transport/planning/outside-haulier-poh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: request.date,
          orderId: request.orderId,
          customerOrderIds: [request.orderId],
          haulier: request.haulier,
          supplierAccountId: plan.needsSupplier ? Number(supplierId) || null : null,
          link: !!link,
          pallets
        })
      });
      const out = await res.json().catch(() => ({}));
      if (out.order_changed) setChanged(true);
      if (!res.ok || !out.success) throw new Error(out.error || `Could not raise the PO-H (${res.status})`);
      setChanged(true);
      notesRef.current = [out.message, plan.board?.text, ...(out.notes || [])].filter(Boolean);
      setSendPo({
        purchaseOrderID: out.purchase_order.xero_purchase_order_id,
        purchaseOrderNumber: out.purchase_order.number
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setRaising(false);
    }
  };

  const openSend = () => {
    if (!plan?.poh) return;
    setSendPo({ purchaseOrderID: plan.poh.xeroId, purchaseOrderNumber: plan.poh.number });
  };

  if (sendPo) {
    return createPortal(
      <SendPOEmailModal
        purchaseOrder={sendPo}
        user={user}
        onClose={() => onClose(true, notesRef.current)}
        onSent={(message) => { notesRef.current = [message, ...notesRef.current.slice(1)].filter(Boolean); }}
      />,
      document.body
    );
  }

  const close = () => onClose(changed, changed ? notesRef.current : []);
  const mode = plan?.mode;
  const raiseTitle = !plan ? 'Checking…' : plan.raiseBlock || 'Change the order to third-party haulage, raise the PO-H and open the Send window';

  return (
    <Modal
      isOpen
      onClose={close}
      title={plan ? `${plan.title} — ${plan.orderNumber}` : `${request.haulier} — ${request.orderNumber || 'the order'}`}
      size="md"
      className="outside-haulier-poh"
      footer={(
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-slideout" onClick={close} disabled={raising}
              title={mode === 'blocked' ? 'Close' : 'Leave the order as it is — not asked again until its haulier changes'}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">{mode === 'blocked' ? 'Close' : 'Not now'}</span>
            </button>
          </div>
          {mode === 'send' && (
            <div>
              <button data-write-action className="btn-slideout" onClick={openSend}
                title={`Open the Send to Supplier window for ${plan.poh?.number}`}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">send</span></div>
                <span className="btn-label">Send PO-H…</span>
              </button>
            </div>
          )}
          {mode === 'raise' && (
            <div>
              <button data-write-action className="btn-slideout" onClick={raise}
                disabled={raising || reloading || !!plan.raiseBlock} title={raiseTitle}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">local_shipping</span></div>
                <span className="btn-label">{raising ? 'Raising…' : 'Raise PO-H'}</span>
              </button>
            </div>
          )}
        </div>
      )}
    >
      <DialogBody
        plan={plan}
        suppliers={suppliers}
        supplierId={supplierId}
        setSupplierId={setSupplierId}
        link={link}
        setLink={setLink}
        pallets={pallets}
        setPallets={setPallets}
        error={error}
        reloading={reloading}
      />
    </Modal>
  );
}

export default OutsideHaulierPohDialog;
